import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FilePlus2, Loader2, Pencil, Trash2 } from "lucide-react";

import API from "../api";
const EMPTY = { slug: "", title: "", excerpt: "", meta_description: "", image: "", body: "", published: true };

export default function JournalManager() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | slug
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () =>
    fetch(`${API}/api/admin/journal`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setPosts(d.posts || []))
      .catch(() => setPosts([]));

  useEffect(() => {
    load();
  }, []);

  const editPost = (p) => {
    setForm({
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt,
      meta_description: p.meta_description,
      image: p.image || "",
      body: (p.body || []).join("\n\n"),
      published: p.published !== false,
    });
    setEditing(p.slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/journal`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          slug: form.slug.trim(),
          image: form.image.trim() || null,
          body: form.body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Could not save the article");
        return;
      }
      toast.success("Article saved");
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch {
      toast.error("Could not save the article");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    const r = await fetch(`${API}/api/admin/journal/${slug}`, { method: "DELETE", credentials: "include" });
    if (r.ok) {
      toast.success("Article deleted");
      load();
    } else {
      toast.error("Could not delete the article");
    }
  };

  const field = (label, key, props = {}) => (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest2 text-gold-2">{label}</span>
      <input
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`journal-${key.replace("_", "-")}-input`}
        className="mt-2 w-full border border-ivory/25 bg-navy px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold focus:outline-none"
        {...props}
      />
    </label>
  );

  return (
    <div className="mt-8" data-testid="journal-manager">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display text-3xl text-ivory">Island Journal</h2>
        <button
          onClick={() => {
            setForm({ ...EMPTY, published: true });
            setEditing("new");
          }}
          data-testid="journal-new-button"
          className="flex items-center gap-2 bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2"
        >
          <FilePlus2 className="h-4 w-4" /> New article
        </button>
      </div>
      <p className="mt-3 max-w-2xl text-sm font-light leading-relaxed text-ivory/70">
        Write and publish SEO articles. Separate paragraphs with a blank line; add links inside
        paragraphs as [label](/path/). Articles appear at /journal/ with their own search snippets.
      </p>

      {editing && (
        <form onSubmit={save} className="mt-6 space-y-4 border border-ivory/15 bg-navy-2 p-6" data-testid="journal-editor">
          <div className="grid gap-4 md:grid-cols-2">
            {field("Title", "title", { required: true, maxLength: 160 })}
            {field("Slug (the page address)", "slug", { required: true, placeholder: "my-article-name" })}
          </div>
          {field("Meta description (150–160 characters)", "meta_description", { required: true, maxLength: 160 })}
          {field("Excerpt (shown on the journal page)", "excerpt", { required: true, maxLength: 400 })}
          {field("Image path (optional, e.g. /images/real/…)", "image")}
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest2 text-gold-2">Article body</span>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={12}
              required
              data-testid="journal-body-input"
              className="mt-2 w-full border border-ivory/25 bg-navy px-4 py-3 text-sm leading-relaxed text-ivory focus:border-gold focus:outline-none"
            />
          </label>
          <label className="flex items-center gap-3 text-sm text-ivory/80">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="journal-published-checkbox" />
            Published (visible on the website)
          </label>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={busy} data-testid="journal-save-button" className="flex items-center gap-2 bg-gold px-7 py-3 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save article
            </button>
            <button type="button" onClick={() => setEditing(null)} className="border border-ivory/25 px-6 py-3 text-xs font-semibold uppercase tracking-widest2 text-ivory/80 hover:text-gold-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 flex flex-col gap-3" data-testid="journal-admin-list">
        {posts.map((p) => (
          <div key={p.slug} className="flex flex-wrap items-center justify-between gap-3 border border-ivory/15 bg-navy-2 px-5 py-4">
            <div>
              <p className="text-base text-ivory">
                {p.title}{" "}
                {!p.published && <span className="ml-2 text-[10px] uppercase tracking-widest2 text-gold-2">Draft</span>}
              </p>
              <p className="text-xs text-ivory/50">/journal/{p.slug}/</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => editPost(p)} data-testid={`journal-edit-${p.slug}`} className="flex items-center gap-2 border border-ivory/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 hover:border-gold hover:text-gold-2">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button onClick={() => remove(p.slug)} data-testid={`journal-delete-${p.slug}`} className="flex items-center gap-2 border border-ivory/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 hover:border-red-400 hover:text-red-400">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}